import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import { Badge } from '@react-apps/design-system';
import type { BadgeProps } from '@react-apps/design-system';
import type {
  TotvsProductLinkAction,
  TotvsSyncStatus,
  TotvsSyncStrategy,
  TotvsWorknowPhase,
} from '../domain/entities/totvs.entity';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/** Rótulo amigável do gateway; cai no valor cru se vier um provider não modelado. */
const PROVIDER_LABELS: Record<string, string> = {
  guru: 'Guru',
  hotmart: 'Hotmart',
};

export function getTotvsProviderLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

export function TotvsProviderBadge({ provider }: { provider: string }) {
  return <Badge variant="default">{getTotvsProviderLabel(provider)}</Badge>;
}

const STATUS_META: Record<
  TotvsSyncStatus,
  { label: string; variant: BadgeVariant }
> = {
  pending: { label: 'Pendente', variant: 'info' },
  in_progress: { label: 'Processando', variant: 'default' },
  waiting_product_link: {
    label: 'Aguardando cadastro do produto',
    variant: 'warning',
  },
  delivered: { label: 'Matriculado', variant: 'success' },
  failed: { label: 'Falhou', variant: 'error' },
  ignored: { label: 'Ignorado', variant: 'warning' },
};

export function TotvsStatusBadge({ status }: { status: TotvsSyncStatus }) {
  const meta = STATUS_META[status] ?? { label: status, variant: 'default' };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export function getTotvsStatusLabel(status: TotvsSyncStatus): string {
  return STATUS_META[status]?.label ?? status;
}

const STRATEGY_META: Record<
  TotvsSyncStrategy,
  { label: string; variant: BadgeVariant }
> = {
  mcb: { label: 'SaveRecord único', variant: 'default' },
  worknow: { label: 'Fórmulas 7+5', variant: 'info' },
};

const WORKNOW_PHASE_LABELS: Record<Exclude<TotvsWorknowPhase, null>, string> = {
  awaiting_baixa: 'aguardando baixa',
};

export function TotvsStrategyBadge({
  strategy,
  worknowPhase,
}: {
  strategy: TotvsSyncStrategy;
  worknowPhase?: TotvsWorknowPhase;
}) {
  const meta = STRATEGY_META[strategy] ?? {
    label: strategy,
    variant: 'default',
  };
  const phaseLabel = worknowPhase ? WORKNOW_PHASE_LABELS[worknowPhase] : null;
  return (
    <Badge variant={meta.variant}>
      {meta.label}
      {phaseLabel ? ` · ${phaseLabel}` : ''}
    </Badge>
  );
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const apiMsg = maybe.response?.data?.message;
    if (typeof apiMsg === 'string') return apiMsg;
    if (Array.isArray(apiMsg) && typeof apiMsg[0] === 'string') {
      return apiMsg[0];
    }
    if (typeof maybe.message === 'string') return maybe.message;
  }
  return fallback;
}

export function Mono({ children }: { children: React.ReactNode }) {
  return <span className="break-all font-mono text-xs">{children}</span>;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
        {label}
      </p>
      <div className="text-(--color-text-primary) text-sm">
        {value === null || value === undefined || value === '' ? (
          <span className="text-(--color-text-secondary)">—</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

/** Card de seção, com título e slot opcional de ação — padrão de `sales`. */
export function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-(--color-text-primary) text-sm font-semibold">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Skeleton de carregamento no formato de card — padrão de `sales`. */
export function SkeletonCard({ rows = 4 }: { rows?: number }) {
  return (
    <div className="border-(--color-border) bg-(--color-bg-primary) space-y-4 rounded-xl border p-6">
      <div className="h-4 w-40 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="h-4 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800"
            style={{ width: `${60 + (i % 3) * 15}%` }}
          />
        ))}
      </div>
    </div>
  );
}

/** Estado de erro/vazio no formato de card centralizado — padrão de `sales`. */
/** Texto de erro inline padronizado (claro + escuro). */
export function ErrorText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-red-600 dark:text-red-400">{children}</p>;
}

/**
 * Link "voltar" com chevron — mesmo visual das telas de detalhe de `sales`.
 * Usa histórico real do browser (`useBackNavigate`) pra preservar `?page=`/
 * filtros da listagem de origem — `to` só é usado como fallback (link aberto
 * direto, refresh, nova aba).
 */
export function BackLink({ to, label }: { to: string; label: string }) {
  const goBack = useBackNavigate(to);
  return (
    <button
      type="button"
      onClick={goBack}
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
    >
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 19.5 8.25 12l7.5-7.5"
        />
      </svg>
      {label}
    </button>
  );
}

/** Badge de situação do de-para (ativo/inativo) — centralizado como os demais. */
/**
 * O destino do de-para. `ignore` é o caso que o operador precisa distinguir num
 * relance na listagem: ele não é um cadastro incompleto, é uma decisão.
 */
export function ProductLinkActionBadge({
  action,
}: {
  action: TotvsProductLinkAction;
}) {
  return action === 'ignore' ? (
    <Badge variant="warning">Ignorar</Badge>
  ) : (
    <Badge variant="info">Matricular</Badge>
  );
}

export function ProductLinkEnabledBadge({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <Badge variant="success">Ativo</Badge>
  ) : (
    <Badge variant="warning">Inativo</Badge>
  );
}
